import {
  apiErrorEnvelopeSchema,
  lookupRequestSchema,
  reputationReportSchema,
  type ApiError,
  type ReputationReport
} from "@sage/shared";

type LookupInput = {
  hash: string;
  token: string | null;
};

export type LookupFailure = {
  kind: "unauthorized" | "quota_exceeded" | "billing_unavailable" | "network_error";
  message: string;
  requestId?: string;
};

export type LookupResult =
  | { ok: true; report: ReputationReport }
  | { ok: false; error: LookupFailure };

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export type ExistenceResult =
  | { found: true; verdict: string }
  | { found: false };

/**
 * Check if a reputation report already exists for a hash (read-only, no quota usage).
 * Uses GET /v1/reputation/reports/:hash/summary — returns found:false on 404.
 */
export async function checkReputationExists(hash: string, token: string | null): Promise<ExistenceResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/reputation/reports/${encodeURIComponent(hash)}/summary`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });

    if (response.status === 404) return { found: false };
    if (!response.ok) return { found: false };

    const body: unknown = await response.json().catch(() => null);
    if (!isRecord(body) || body.ok !== true || !isRecord(body.data)) return { found: false };

    const verdict = typeof body.data.verdict === "string" ? body.data.verdict : "unknown";
    return { found: true, verdict };
  } catch {
    return { found: false };
  }
}

export async function lookupReputation(input: LookupInput): Promise<LookupResult> {
  const payload = lookupRequestSchema.parse({ hash: input.hash });

  try {
    const response = await fetch(`${API_BASE_URL}/v1/reputation/lookups`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(input.token ? { Authorization: `Bearer ${input.token}` } : {})
      },
      body: JSON.stringify(payload)
    });

    const body: unknown = await response.json().catch(() => null);

    if (!response.ok) {
      return { ok: false, error: mapErrorEnvelope(body, response.status) };
    }

    const report = parseLookupReport(body);

    if (!report) {
      return {
        ok: false,
        error: {
          kind: "network_error",
          message: "The reputation API returned an unexpected response."
        }
      };
    }

    return { ok: true, report };
  } catch {
    return {
      ok: false,
      error: {
        kind: "network_error",
        message: "Unable to reach the reputation API. Check the network or API base URL."
      }
    };
  }
}

export function mapApiError(error: Pick<ApiError, "code" | "message" | "requestId">, status: number): LookupFailure {
  const code = error.code.toUpperCase();

  if (status === 401 || status === 403 || code === "UNAUTHENTICATED" || code === "UNAUTHORIZED") {
    return {
      kind: "unauthorized",
      message: "Sign in to run private reputation lookups.",
      requestId: error.requestId
    };
  }

  if (status === 402 || status === 429 || code.includes("QUOTA")) {
    return {
      kind: "quota_exceeded",
      message: "Hash lookup quota exceeded. Upgrade or wait for the quota window to reset.",
      requestId: error.requestId
    };
  }

  if (status === 503 || code.includes("BILLING")) {
    return {
      kind: "billing_unavailable",
      message: "Billing and entitlement checks are temporarily unavailable.",
      requestId: error.requestId
    };
  }

  return {
    kind: "network_error",
    message: error.message,
    requestId: error.requestId
  };
}

function mapErrorEnvelope(body: unknown, status: number): LookupFailure {
  const parsed = apiErrorEnvelopeSchema.safeParse(body);

  if (parsed.success) {
    return mapApiError(parsed.data.error, status);
  }

  if (status === 401 || status === 403) {
    return { kind: "unauthorized", message: "Sign in to run private reputation lookups." };
  }

  return {
    kind: "network_error",
    message: "The reputation API request failed."
  };
}

function parseLookupReport(body: unknown) {
  if (!isRecord(body) || body.ok !== true || !isRecord(body.data)) {
    return null;
  }

  const parsed = reputationReportSchema.safeParse(body.data.report);
  return parsed.success ? parsed.data : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
